import type { Elements } from "../data/schema";
import { DEG, centuriesSinceJ2000, wrapTwoPi } from "./time";
import type { OrbitState } from "./kepler";

const clampE = (e: number): number => Math.min(Math.max(e, 0), 0.999999);

/** Elements valid at jd, converted to km and radians, in the omega/M representation. */
export function orbitStateAt(el: Elements, jd: number, out: OrbitState): OrbitState {
  const T = centuriesSinceJ2000(jd);
  const r = el.rates;
  const a = el.a + (r ? r.a * T : 0);
  const e = clampE(el.e + (r ? r.e * T : 0));
  const i = el.i + (r ? r.i * T : 0);
  const node = el.node + (r ? r.node * T : 0);
  let omega: number;
  let M: number;
  if (el.L0 !== undefined && el.varpi !== undefined) {
    const L = el.L0 + (r ? r.L * T : 0);
    const varpi = el.varpi + (r ? r.varpi * T : 0);
    omega = varpi - node;
    M = L - varpi;
    if (el.correction) {
      const { b, c, s, f } = el.correction;
      M += b * T * T + c * Math.cos(f * T * DEG) + s * Math.sin(f * T * DEG);
    }
  } else {
    const meanMotionDegPerDay = 360 / el.periodDays;
    omega = el.omega ?? 0;
    M = (el.m0 ?? 0) + meanMotionDegPerDay * (jd - el.epochJd);
  }
  out.a = a;
  out.e = e;
  out.i = i * DEG;
  out.omega = omega * DEG;
  out.node = node * DEG;
  out.M = wrapTwoPi(M * DEG);
  return out;
}
